/**
 * 组装 Temu 本土发品 {@code temu.local.goods.v3.add} 业务参数。
 * 对齐 temu-GLOBAL / temu-api-US「Product Publishing API V3」：
 * goodsBasic + attributes + skuList（与跨境 bg.goods.add 字段族完全不同）。
 */

import { isBlankTemuAttributeValueId, resolveSkcColorAgainstTemplate } from '@/temu-platform/utils/temuAttributeTemplate';
import { isEditorAttrVisible } from '@/temu-platform/utils/temuAttrVisibility';

function toInt(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : fallback;
}

function moneyAmount(majorUnit) {
  const n = Number(majorUnit);
  if (!Number.isFinite(n) || n < 0) return '0';
  // 保留最多 2 位小数，去掉无意义尾零
  return String(Number(n.toFixed(2)));
}

function dimCm(sku, keys, fallback) {
  for (const k of keys) {
    if (sku?.[k] != null && String(sku[k]).trim() !== '') {
      const n = Number(sku[k]);
      if (Number.isFinite(n) && n > 0) return String(n);
    }
  }
  // mm → cm
  for (const k of ['lengthMm', 'widthMm', 'heightMm']) {
    if (keys.some((x) => x.toLowerCase().includes(k.replace('Mm', ''))) && sku?.[k] != null) {
      const n = Number(sku[k]);
      if (Number.isFinite(n) && n > 0) return String(Number((n / 10).toFixed(1)));
    }
  }
  return String(fallback);
}

function weightGrams(sku) {
  if (sku?.weightMg != null && String(sku.weightMg).trim() !== '') {
    const mg = Number(sku.weightMg);
    if (Number.isFinite(mg) && mg > 0) return String(Math.max(1, Math.round(mg / 1000)));
  }
  const grams = Number(sku?.inputWeight || sku?.weightGrams || sku?.weightUi || sku?.weight);
  if (Number.isFinite(grams) && grams > 0) return String(Math.round(grams));
  return '100';
}

/** V3 允许任意可下载 URL；优先 Temu CDN，其次公开可访问地址 */
function imageUrlAny(imageOrUrl) {
  if (!imageOrUrl) return '';
  if (typeof imageOrUrl === 'string') return String(imageOrUrl).trim();
  return String(
    imageOrUrl.temuUrl
    || imageOrUrl.publicUrl
    || imageOrUrl.ipeUrl
    || imageOrUrl.previewUrl
    || imageOrUrl.url
    || '',
  ).trim();
}

function collectCarouselUrls(form) {
  const urls = [];
  const push = (img) => {
    const u = imageUrlAny(img);
    if (u && !urls.includes(u)) urls.push(u);
  };
  (form.skcList || []).forEach((skc) => {
    push(skc.mainImage);
    (skc.detailImages || []).forEach(push);
    Object.values(skc.showroomSlots || {}).forEach(push);
  });
  (form.carouselImages || []).forEach(push);
  return urls.slice(0, 10);
}

function collectDetailUrls(form) {
  const urls = [];
  const push = (img) => {
    const u = imageUrlAny(img);
    if (u && !urls.includes(u)) urls.push(u);
  };
  (form.skcList || []).forEach((skc) => {
    (skc.detailImages || []).forEach(push);
  });
  (form.detailImages || []).forEach(push);
  return urls.slice(0, 50);
}

function resolveCurrency(form, sku) {
  return String(
    sku?.currencyType
    || sku?.currency
    || form?.currency
    || form?.publishCurrency
    || 'USD',
  ).trim().toUpperCase() || 'USD';
}

function resolveSkuPriceMajor(sku, form) {
  const candidates = [
    sku?.supplierPriceYuan,
    sku?.supplyPrice,
    sku?.price,
    sku?.salePrice,
    sku?.supplierPrice,
    form?.defaultPriceYuan,
    form?.supplyPrice,
  ];
  for (const c of candidates) {
    if (c == null || String(c).trim() === '') continue;
    const n = Number(c);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function resolveShipmentLimitDay(form) {
  if (form?.shipmentLimitDay != null && String(form.shipmentLimitDay).trim() !== '') {
    const d = toInt(form.shipmentLimitDay, 0);
    if (d > 0) return d;
  }
  const sec = toInt(form?.shipmentLimitSecond, 0);
  if (sec > 0) return Math.max(1, Math.ceil(sec / 86400));
  return undefined;
}

function buildAttributes(form, attrTpl) {
  const out = [];
  const attrs = form.productAttributes && typeof form.productAttributes === 'object'
    ? form.productAttributes
    : {};
  const pool = [
    ...(attrTpl?.productAttrs || []),
    ...(attrTpl?.requiredProduct || []),
    ...(attrTpl?.optionalProduct || []),
  ];
  Object.entries(attrs).forEach(([attributeId, value]) => {
    if (value == null || value === '') return;
    const editorAttr = pool.find((a) => String(a?.attributeId) === String(attributeId));
    if (editorAttr && !isEditorAttrVisible(editorAttr, attrs, pool)) return;
    if (editorAttr?.temu_prop?.isSale) return;
    const name = String(editorAttr?.name || editorAttr?.temu_prop?.name || attributeId).trim();
    if (!name) return;
    const values = Array.isArray(value) ? value : [value];
    const valueTexts = [];
    values.forEach((entry) => {
      if (entry == null || entry === '') return;
      if (typeof entry === 'object') {
        const t = String(
          entry.propValue
          || entry.name
          || entry.label
          || entry.value
          || (entry.percent != null ? entry.percent : '')
          || '',
        ).trim();
        if (t) valueTexts.push(t.slice(0, 128));
        return;
      }
      const t = String(entry).trim();
      if (t) valueTexts.push(t.slice(0, 128));
    });
    if (!valueTexts.length) return;
    out.push({ name: name.slice(0, 128), value: valueTexts.slice(0, 1000) });
  });
  return out.slice(0, 200);
}

function buildSkuVariations(skc, sku, colorAttr, sizeAttr) {
  const variations = [];
  const resolved = resolveSkcColorAgainstTemplate(skc, colorAttr);
  const colorName = String(colorAttr?.name || 'Color').trim() || 'Color';
  const colorVal = String(resolved.colorLabel || resolved.colorExtraValue || skc?.colorLabel || '').trim();
  if (colorVal) {
    variations.push({ Name: colorName.slice(0, 128), value: colorVal.slice(0, 128) });
  }
  const sizeName = String(sizeAttr?.name || 'Size').trim() || 'Size';
  const sizeVal = String(sku?.sizeLabel || sku?.sizeExtraValue || '').trim();
  if (sizeVal) {
    variations.push({ Name: sizeName.slice(0, 128), value: sizeVal.slice(0, 128) });
  }
  if (!variations.length) {
    variations.push({ Name: 'Default', value: 'Default' });
  }
  return variations.slice(0, 5);
}

/**
 * @param {object} form Editor 表单（已解析 catIds / 标题等）
 * @param {{ attrTpl?: object, categories?: array }} opts
 */
export function buildTemuLocalGoodsV3Payload(form, opts = {}) {
  const attrTpl = opts.attrTpl || null;
  const goodsName = String(form.productName || form.title || '').trim();
  if (!goodsName) {
    throw new Error('请填写商品名称');
  }

  const externalGoodsId = String(
    form.externalGoodsId
    || form.supplierCode
    || form.extCode
    || form.outGoodsSn
    || '',
  ).trim().slice(0, 128);
  if (!externalGoodsId) {
    throw new Error('请填写货号（外部商品 ID），本土发品必填 externalGoodsId');
  }

  const carousel = collectCarouselUrls(form);
  const detailImage = collectDetailUrls(form);
  if (!carousel.length) {
    throw new Error('请至少上传 1 张商品主图（本土 V3 会按 URL 自动转存）');
  }

  const titleEn = String(form.titleEn || form.englishTitle || '').trim();
  const goodsDesc = String(form.description || form.goodsDesc || form.detailHtml || '').trim();
  const catLeafName = String(
    form.categoryName
    || form.extCatName
    || form.leafCategoryName
    || '',
  ).trim();

  const goodsBasic = {
    externalGoodsId,
    goodsName: goodsName.slice(0, 500),
    goodsCarouselImage: carousel,
  };
  if (catLeafName) goodsBasic.extCatName = catLeafName.slice(0, 500);
  if (goodsDesc) goodsBasic.goodsDesc = goodsDesc.slice(0, 10000);
  if (detailImage.length) goodsBasic.detailImage = detailImage;

  const customOn = Number(form.isCustomProduct ?? form.personalizationSwitch) === 1;
  goodsBasic.productType = customOn ? 2 : toInt(form.productType, 1) || 1;

  const shipmentLimitDay = resolveShipmentLimitDay(form);
  if (shipmentLimitDay != null) {
    goodsBasic.shipmentLimitDay = shipmentLimitDay;
  }

  const colorAttr = attrTpl?.skcSaleAttrs?.[0];
  const sizeAttr = attrTpl?.skuSaleAttrs?.[0];
  const skcSource = (Array.isArray(form.skcList) ? form.skcList : []).filter((skc) => (
    !isBlankTemuAttributeValueId(skc?.colorValueId)
    || String(skc?.colorExtraValue || skc?.colorLabel || '').trim()
    || !(colorAttr)
  ));
  const skcList = skcSource.length ? skcSource : [{}];

  const skuList = [];
  skcList.forEach((skc, skcIndex) => {
    const skuRows = (skc.skuRows || []).length ? skc.skuRows : [{}];
    skuRows.forEach((sku, skuIndex) => {
      const externalSkuId = String(
        sku.externalSkuId
        || sku.supplierSku
        || sku.extCode
        || sku.skuSn
        || `${externalGoodsId}-${skcIndex + 1}-${skuIndex + 1}`,
      ).trim().slice(0, 128);
      if (!externalSkuId) {
        throw new Error('SKU 缺少外部编码 externalSkuId');
      }

      const images = [];
      const pushImg = (img) => {
        const u = imageUrlAny(img);
        if (u && !images.includes(u)) images.push(u);
      };
      pushImg(sku.thumbImage || sku.mainImage);
      pushImg(skc.mainImage);
      (skc.detailImages || []).slice(0, 4).forEach(pushImg);
      carousel.forEach(pushImg);
      if (!images.length) {
        throw new Error(`SKU「${externalSkuId}」缺少图片`);
      }

      const currency = resolveCurrency(form, sku);
      const priceMajor = resolveSkuPriceMajor(sku, form);
      const quantity = toInt(sku.stock ?? form.stockQty ?? form.defaultStock ?? 0, 0);

      const packageInfo = {
        weight: weightGrams(sku),
        length: dimCm(sku, ['inputLen', 'length', 'len'], 10),
        width: dimCm(sku, ['inputWidth', 'width'], 20),
        height: dimCm(sku, ['inputHeight', 'height'], 30),
      };

      const row = {
        externalSkuId,
        images: images.slice(0, 10),
        price: {
          basePrice: {
            amount: moneyAmount(priceMajor),
            currency,
          },
        },
        quantity,
        packageInfo,
        variations: buildSkuVariations(skc, sku, colorAttr, sizeAttr),
      };

      const listPriceMajor = Number(sku.listPrice ?? sku.suggestedPrice ?? sku.marketPrice);
      if (Number.isFinite(listPriceMajor) && listPriceMajor > 0) {
        row.price.listPrice = {
          amount: moneyAmount(listPriceMajor),
          currency,
        };
      }

      const barcode = String(sku.barcode || sku.barCode || sku.ean || '').trim();
      if (barcode) {
        row.barCode = {
          barCodeType: String(sku.barCodeType || 'EAN').trim() || 'EAN',
          barCodeId: [barcode],
        };
      }

      skuList.push(row);
    });
  });

  if (!skuList.length) {
    throw new Error('请至少配置 1 个 SKU');
  }
  if (skuList.length > 500) {
    throw new Error('本土发品单次最多 500 个 SKU');
  }

  const payload = {
    goodsBasic,
    skuList,
  };

  const attributes = buildAttributes(form, attrTpl);
  if (attributes.length) {
    payload.attributes = attributes;
  }

  // 语言提示：有英文标题时声明 en，便于平台优先用 ERP 语言
  if (titleEn) {
    payload.language = { default: 'en' };
    if (titleEn !== goodsName) {
      // 英文名写入描述前缀，避免丢失（V3 无独立 i18n 标题字段）
      const desc = String(goodsBasic.goodsDesc || '');
      if (!desc.includes(titleEn)) {
        goodsBasic.goodsDesc = [titleEn, desc].filter(Boolean).join('\n').slice(0, 10000);
      }
    }
  }

  return payload;
}
